import { Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatTabNavPanel } from '@angular/material/tabs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RxState } from '@rx-angular/state';
import { distinctUntilChanged, filter, map, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';

import {
    GLOBAL_RX_STATE,
    GlobalState,
    GlobalStateSelectors,
} from '@data-terminal/data-access/global-state/global-rx.state';
import { APP_NAME, HEI_CC_URL, IS_ROUTER_IN_LOADING_STATE } from '@data-terminal/data-access/injection-tokens';
import { isNavigationHidden, NavigationService } from '@data-terminal/data-access/navigation/navigation.service';
import { OperationNavigationService } from '@data-terminal/data-access/navigation/operation-navigation.service';
import { OnPremMessagesService } from '@data-terminal/data-access/on-prem-messages/on-prem-messages.service';
import { OrganizationService } from '@data-terminal/data-access/organization';
import { CloudSessionService, DataTerminalSessionService } from '@data-terminal/data-access/session-service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { OperationCardAttributeOrderService } from '@data-terminal/feature-settings/services/operation-card-attirbute-order/operation-card-attribute-order.service';
import { SettingsConfirmationService } from '@data-terminal/feature-settings/services/settings-confirmation/settings-confirmation.service';
import { SettingsDataTransferService } from '@data-terminal/feature-settings/services/settings-data-transfer/settings-data-transfer.service';
import { GeneralUserSettingsService } from '@data-terminal/feature-settings/services/settings-general/general-user-settings/general-user-settings.service';
import { TransitionService } from '@data-terminal/feature-transition/services';
import { EmployeeService } from '@data-terminal/feature-workstation-details/services/employee/employee.service';
import { GeneralActivitiesService } from '@data-terminal/feature-workstation-details/services/general-activities/general-activities.service';
import { WebsocketService } from '@data-terminal/feature-workstation-details/services/websocket/websocket.service';
import { ROUTE_PARAMS, ROUTE_PATH } from '@data-terminal/shared-models/navigation';
import { ActivityRequired } from '@data-terminal/shared-models/operation';
import { ShutdownBlockerService } from '@data-terminal/ui-smart/services';
import { NetworkStatusService } from '@data-terminal/ui-smart/services/network-status.service';
import { getParams } from '@data-terminal/utils/helpers';
import { DeviceTypeService, LoadingStateService, ResizeService } from '@data-terminal/utils/services';
import { CCAuthService, CCSession } from '@heidelberg/control-center-frontend-integration/auth';
import {
    HdmuiMainApplicationHeaderUser,
    HdmuiMembership,
    IconRegistryService,
    LAUNCH_SCREEN,
    LaunchScreen,
} from '@heidelberg/hdmui-angular';
import { ChatBotContext } from '@heidelberg/hplus-ngx-chatbot-integration';
import { LoggingService } from '../../services/logging.service';
import { SettingsTimeRecordingService } from '@data-terminal/feature-settings/services/settings-time-recording/settings-time-recording.service';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy {
    private readonly launchScreen = inject<LaunchScreen>(LAUNCH_SCREEN);
    isRouterInLoadingState$ = inject<Observable<boolean>>(IS_ROUTER_IN_LOADING_STATE);
    private readonly globalState = inject<RxState<GlobalState>>(GLOBAL_RX_STATE);
    private readonly heiCCUrl = inject(HEI_CC_URL);
    private readonly loadingStateService = inject(LoadingStateService);
    private readonly ccAuthService = inject(CCAuthService);
    private readonly navigationService = inject(NavigationService);
    private readonly sessionService = inject(DataTerminalSessionService);
    private readonly cloudSessionService = inject(CloudSessionService);
    private readonly transitionService = inject(TransitionService);
    private readonly userMachinesService = inject(UserMachinesService);
    private readonly settingsConfirmationService = inject(SettingsConfirmationService);
    private readonly settingsDataTransferService = inject(SettingsDataTransferService);
    private readonly generalUserSettingsService = inject(GeneralUserSettingsService);
    private readonly settingsTimeRecordingService = inject(SettingsTimeRecordingService);
    private readonly operationCardAttributeOrderService = inject(OperationCardAttributeOrderService);
    private readonly employeeService = inject(EmployeeService);
    private readonly router = inject(Router);
    private readonly generalActivitiesService = inject(GeneralActivitiesService);
    readonly activatedRoute = inject(ActivatedRoute);
    readonly deviceTypeService = inject(DeviceTypeService);
    private readonly organizationService = inject(OrganizationService);
    private readonly resizeService = inject(ResizeService);
    private readonly operationNavigationService = inject(OperationNavigationService);
    private readonly websocketService = inject(WebsocketService);
    private readonly networkStatusService = inject(NetworkStatusService);
    private readonly shutdownBlockerService = inject(ShutdownBlockerService);
    private readonly iconRegistryService = inject(IconRegistryService);
    private readonly loggingService = inject(LoggingService);
    private readonly onPremMessagesService = inject(OnPremMessagesService);

    @ViewChild('tabPanel') tabPanel!: MatTabNavPanel;

    public cloudUser!: HdmuiMainApplicationHeaderUser;
    public initialLoadDone = false;
    readonly chatContext: ChatBotContext = { app: inject(APP_NAME) };

    public showNavigation$: Observable<boolean>;
    public isLoading$: Observable<boolean>;
    public isProfileLoading$: Observable<boolean>;
    public hideBasicUIElements$: Observable<boolean>;
    public blockerActive$: Observable<boolean>;
    public isContinuousScanningEnabled$: Observable<boolean>;

    public readonly userMachines$ = this.userMachinesService.getUserMachines$();
    public readonly isTablet$ = this.resizeService.isTablet$;

    public breakpoints = [
        Breakpoints.HandsetPortrait,
        Breakpoints.TabletPortrait,
        Breakpoints.WebPortrait,
        Breakpoints.HandsetLandscape,
        Breakpoints.TabletLandscape,
        Breakpoints.WebLandscape,
    ];

    private readonly subscriptions = new Subscription();

    constructor() {
        this.networkStatusService.initNetworkListener();
        this.isProfileLoading$ = this.loadingStateService.isProfileLoading$;
        this.isLoading$ = this.loadingStateService.isLoading$;
        this.showNavigation$ = this.navigationService.navData$.pipe(isNavigationHidden);
        this.hideBasicUIElements$ = this.shouldHideUIElements$();
        this.blockerActive$ = this.shutdownBlockerService.blockerActive$;
        this.isContinuousScanningEnabled$ = this.generalUserSettingsService
            .getGeneralUserSettings$()
            .pipe(map((generalSettings) => generalSettings.cameraSettings.continuousQrScanning));
        this.iconRegistryService.registerWatermark('../../../../assets/icons/HDM-Data-Capture-Cutout.svg');
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public ngAfterViewInit(): void {
        const sessionLoadReady = this.ccAuthService.isInitialLoadFinished();

        if (sessionLoadReady) {
            this.launchScreen.hide();
        }

        setTimeout(() => {
            this.initialLoadDone = this.ccAuthService.isInitialLoadFinished();
        }, 100);

        const userMail = this.ccAuthService.getCurrentUser()?.email;
        const orgName = this.ccAuthService.getCurrentMembership()?.organization.name;
        const orgId = this.ccAuthService.getCurrentMembership()?.organizationId;

        if (userMail && orgName && orgId) {
            this.ccAuthService.currentSession$
                .pipe(
                    tap((session) => {
                        this.cloudUser = this.getToolbarUserFromSession(session);
                    }),
                    switchMap(() => this.sessionService.logInToDataTerminal(userMail, orgName)),
                    take(1),
                    switchMap(() =>
                        this.skipIfUserNotFound(this.cloudSessionService.getLatestUserSession().pipe(take(1)))
                    ),
                    tap(() => {
                        if (this.sessionService.getCurrentDataTerminalUser()) {
                            this.sessionService.keepAlive();
                        }
                    }),
                    switchMap(() =>
                        this.skipIfUserNotFound(this.settingsConfirmationService.fetchConfirmationSettings())
                    ),
                    switchMap(() =>
                        this.skipIfUserNotFound(
                            this.settingsDataTransferService
                                .fetchDataTransferSettings()
                                .pipe(
                                    tap((dataTransferSettings) =>
                                        this.loggingService.changeLoggingState(dataTransferSettings.clientSideDebugging)
                                    )
                                )
                        )
                    ),
                    switchMap(() =>
                        this.skipIfUserNotFound(this.generalUserSettingsService.fetchGeneralUserSettings())
                    ),
                    switchMap(() =>
                        this.skipIfUserNotFound(this.settingsTimeRecordingService.fetchTimeRecordingSettings())
                    ),
                    switchMap(() =>
                        this.skipIfUserNotFound(this.operationCardAttributeOrderService.fetchOperationCardAttributes())
                    ),
                    switchMap(() => this.organizationService.fetchOrganizationInfo()),
                    switchMap(() => this.organizationService.fetchOrganizationWhitelist()),
                    switchMap(() => this.organizationService.fetchBackendVersion()),
                    switchMap(() => this.transitionService.shouldDisplayWelcomeScreen$),
                    tap((displayWelcomeScreen) =>
                        displayWelcomeScreen
                            ? this.transitionService.navigateToWelcomePage()
                            : this.navigationService.redirectToInitialRoute()
                    )
                )
                .subscribe({
                    complete: () => {
                        this.navigationService.initRoutingTracking();
                        this.loadingStateService.setProfileLoading(false);
                        if (this.sessionService.getCurrentDataTerminalUser()) {
                            this.websocketService.connectToWebsocket();
                            this.shutdownBlockerService.subscribeToOnPremStatus();
                            this.userMachinesService.subscribeToUserMachines();
                            this.generalActivitiesService.subscribeToUserActivities();
                            this.onPremMessagesService.initializeOnPremMessageService(
                                this.organizationService.organizationInfo.onPremVersion
                            );
                        }
                        this.fetchAllEmployees();
                        this.shouldSetOperationNavigationTab();
                    },
                });
        }
    }

    public onLogout(): void {
        this.ccAuthService.signOut();
    }

    private getToolbarUserFromSession(s: CCSession): HdmuiMainApplicationHeaderUser {
        const orgId = s.activeMembership?.organizationId;
        return {
            id: s.user.id,
            fullName: s.user.fullName,
            avatarUrl: s.user.avatarUrl ?? undefined,
            activeOrganizationId: orgId ?? '',
            memberships:
                Object.keys(s.user.memberships).length > 0
                    ? s.user.memberships.map((m) => {
                          const orgMembership: HdmuiMembership = {
                              id: m.id,
                              organization: {
                                  id: m.organizationId,
                                  name: `${m.organization.name}`,
                                  logoUrl: m.organization.logoUrl ?? undefined,
                              },
                              roles: m.ccRoles,
                          };
                          return orgMembership;
                      })
                    : [],
        };
    }

    private shouldSetOperationNavigationTab(): void {
        this.router.events
            .pipe(
                untilDestroyed(this),
                filter((event) => event instanceof NavigationEnd),
                map((event) => event as NavigationEnd),
                filter(({ url }) => !url.endsWith(`/${ROUTE_PATH.timeLogging}`)),
                map(() => {
                    const params = getParams(this.activatedRoute);

                    return params[ROUTE_PARAMS.machineId];
                }),
                filter((machineId) => !!machineId),
                distinctUntilChanged(),
                switchMap((machineId) => {
                    return this.userMachinesService.getUserMachines$().pipe(
                        map((userMachines) => {
                            return userMachines.allMachines.find((machine) => machine.machineId === machineId);
                        })
                    );
                })
            )
            .subscribe((currentMachine) => {
                if (currentMachine) {
                    const activeJob = currentMachine.runningOperation;
                    if (activeJob) {
                        const isOperation = activeJob.primaryKey?.length > 0;
                        this.operationNavigationService.setInitialState({
                            [ActivityRequired.OPERATION]: {
                                isDisabled: false,
                                badge: isOperation ? 1 : 0,
                            },
                            [ActivityRequired.OTHER_ACTIVITY]: {
                                isDisabled: isOperation,
                                badge: isOperation ? 0 : 1,
                            },
                        });
                        return;
                    }
                }
                this.operationNavigationService.setInitialState({
                    [ActivityRequired.OPERATION]: {
                        isDisabled: false,
                        badge: 0,
                    },
                    [ActivityRequired.OTHER_ACTIVITY]: {
                        isDisabled: false,
                        badge: 0,
                    },
                });
            });
    }

    private shouldHideUIElements$(): Observable<boolean> {
        return this.router.events.pipe(
            untilDestroyed(this),
            filter((event) => event instanceof NavigationEnd),
            map((event) => event as NavigationEnd),
            map(({ url }) => {
                return url.endsWith(`/${ROUTE_PATH.welcome}`) || url.includes(`/${ROUTE_PATH.farewell}`);
            })
        );
    }

    private fetchAllEmployees(): void {
        this.globalState.connect(GlobalStateSelectors.All_EMPLOYEES, this.employeeService.fetchEmployees());
    }

    private skipIfUserNotFound<T>(observable: Observable<T>): Observable<T | boolean> {
        if (this.sessionService.getCurrentDataTerminalUser()) {
            return observable;
        } else {
            return of(false);
        }
    }

    public onScreenBlockerRedirect(): void {
        window.location.href = this.heiCCUrl;
    }
}
